<template>
  <div
    class="prepeers-video"
    :class="`prepeers-video--${props.aspectRatio}`">
    <img
      v-if="resolvedPoster"
      :src="resolvedPoster"
      :alt="props.title"
      class="prepeers-video__poster"
      loading="lazy"
      decoding="async"
      @error="onPosterError" />
    <div
      v-else
      class="prepeers-video__poster-fallback"
      aria-hidden="true" />

    <div
      class="prepeers-video__shade"
      aria-hidden="true" />

    <button
      type="button"
      class="prepeers-video__play-button"
      :aria-label="props.title"
      :aria-disabled="!hasPlayableSource"
      @click="openModal">
      <span class="prepeers-video__play-circle">
        <PrePeersIcon
          name="play"
          class="prepeers-video__play-icon" />
      </span>
    </button>
  </div>

  <Teleport
    v-if="isMounted"
    to="body">
    <Transition name="prepeers-video-modal">
      <div
        v-if="isModalOpen"
        class="prepeers-video__modal"
        role="dialog"
        aria-modal="true"
        :aria-label="props.title"
        @click.self="closeModal">
        <button
          type="button"
          class="prepeers-video__close-button"
          :aria-label="$t('video.closeAriaLabel')"
          @click="closeModal">
          <PrePeersIcon name="close" />
        </button>

        <div
          class="prepeers-video__modal-content"
          @click.stop>
          <div
            class="prepeers-video__player"
            :class="`prepeers-video__player--${props.aspectRatio}`">
            <iframe
              v-if="props.youtubeId"
              :src="youtubeEmbedUrl"
              :title="props.title"
              class="prepeers-video__media"
              allow="
                accelerometer;
                autoplay;
                clipboard-write;
                encrypted-media;
                gyroscope;
                picture-in-picture;
                web-share;
              "
              allowfullscreen />

            <video
              v-else-if="props.src"
              :src="props.src"
              :poster="props.poster"
              :title="props.title"
              class="prepeers-video__media prepeers-video__media--contain"
              controls
              autoplay
              playsinline />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType, Ref } from 'vue'
import type {
  PrePeersVideoAspectRatio,
  PrePeersVideoEmit,
  PrePeersVideoProps,
} from '~/types/PrePeersVideo'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import PrePeersIcon from '~/components/icons/PrePeersIcon.vue'

const props: PrePeersVideoProps = defineProps({
  title: {
    type: String,
    required: true,
  },
  src: {
    type: String,
    default: undefined,
  },
  youtubeId: {
    type: String,
    default: undefined,
  },
  poster: {
    type: String,
    default: undefined,
  },
  aspectRatio: {
    type: String as PropType<PrePeersVideoAspectRatio>,
    default: 'square',
  },
})

const emit: PrePeersVideoEmit = defineEmits<{
  (event: 'open'): void
}>()

const isModalOpen: Ref<boolean> = ref(false)
const hasHdThumbnailFailed: Ref<boolean> = ref(false)
const isMounted: Ref<boolean> = ref(false)

const hasPlayableSource: ComputedRef<boolean> = computed(
  (): boolean => Boolean(props.src) || Boolean(props.youtubeId),
)

const resolvedPoster: ComputedRef<string | undefined> = computed((): string | undefined => {
  if (props.poster) return props.poster
  if (!props.youtubeId) return undefined

  const quality: string = hasHdThumbnailFailed.value ? 'hqdefault' : 'maxresdefault'
  return `https://img.youtube.com/vi/${props.youtubeId}/${quality}.jpg`
})

const youtubeEmbedUrl: ComputedRef<string> = computed((): string => {
  if (!props.youtubeId) return ''

  return `https://www.youtube.com/embed/${props.youtubeId}?autoplay=1&rel=0&modestbranding=1`
})

/**
 * Bascule sur la miniature de repli lorsque l'image `maxresdefault` ne se charge pas.
 * @returns {void}
 */
function onPosterError(): void {
  hasHdThumbnailFailed.value = true
}

/**
 * Ouvre la fenêtre de lecture si une source jouable est configurée.
 * @returns {void}
 */
function openModal(): void {
  if (!hasPlayableSource.value) return

  isModalOpen.value = true
  emit('open')
}

/**
 * Ferme la fenêtre de lecture.
 * @returns {void}
 */
function closeModal(): void {
  isModalOpen.value = false
}

/**
 * Ferme la fenêtre de lecture avec la touche Échap.
 * @param {KeyboardEvent} event - Touche pressée.
 * @returns {void}
 */
function onDocumentKeydown(event: KeyboardEvent): void {
  if (isModalOpen.value && event.key === 'Escape') closeModal()
}

watch(isModalOpen, (isOpen: boolean): void => {
  document.body.style.overflow = isOpen ? 'hidden' : ''
})

onMounted((): void => {
  isMounted.value = true
  document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount((): void => {
  document.body.style.overflow = ''
  document.removeEventListener('keydown', onDocumentKeydown)
})
</script>

<style scoped>
.prepeers-video {
  position: relative;
  isolation: isolate;
  display: block;
  overflow: hidden;
  background: var(--bg-surface);
}

.prepeers-video--video {
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-xl);
}

.prepeers-video--portrait {
  aspect-ratio: 3 / 4;
  border-radius: var(--radius-xl);
}

.prepeers-video--square {
  aspect-ratio: 1 / 1;
  border-radius: 28%;
}

.prepeers-video__poster,
.prepeers-video__poster-fallback,
.prepeers-video__shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.prepeers-video__poster {
  object-fit: cover;
  transition: transform var(--duration-slow) var(--ease-out);
}

.prepeers-video:hover .prepeers-video__poster {
  transform: scale(1.05);
}

.prepeers-video__poster-fallback {
  background: var(--gradient-blue);
}

.prepeers-video__shade {
  pointer-events: none;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1), transparent);
}

.prepeers-video__play-button {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}

.prepeers-video__play-button:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 4px rgba(255, 255, 255, 0.7);
}

.prepeers-video__play-button[aria-disabled='true'] {
  cursor: not-allowed;
}

.prepeers-video__play-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(12px);
  color: var(--text-inverse);
  transition: transform var(--duration-base) var(--ease-out);
}

.prepeers-video:hover .prepeers-video__play-circle {
  transform: scale(1.1);
}

.prepeers-video__play-icon {
  width: 32px;
  height: 32px;
  margin-left: 2px;
}

.prepeers-video__modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(4px);
}

.prepeers-video__close-button {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  z-index: 1;
  display: flex;
  padding: var(--space-3);
  border: 0;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
  cursor: pointer;
  transition:
    transform var(--duration-base) var(--ease-out),
    background var(--duration-base) var(--ease-out);
}

.prepeers-video__close-button:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2);
}

.prepeers-video__close-button :deep(.prepeers-icon) {
  width: 24px;
  height: 24px;
}

.prepeers-video__modal-content {
  position: relative;
  width: 100%;
  max-width: 64rem;
}

.prepeers-video__player {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--bg-base);
}

.prepeers-video__player--video {
  aspect-ratio: 16 / 9;
}

.prepeers-video__player--portrait {
  aspect-ratio: 3 / 4;
}

.prepeers-video__player--square {
  aspect-ratio: 1 / 1;
}

.prepeers-video__media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.prepeers-video__media--contain {
  object-fit: contain;
}

.prepeers-video-modal-enter-active {
  transition: opacity var(--duration-fast) var(--ease-out);
}

.prepeers-video-modal-leave-active {
  transition: opacity var(--duration-fast) var(--ease-in-out);
}

.prepeers-video-modal-enter-from,
.prepeers-video-modal-leave-to {
  opacity: 0;
}

@media (min-width: 640px) {
  .prepeers-video__play-circle {
    width: 96px;
    height: 96px;
  }

  .prepeers-video__play-icon {
    width: 40px;
    height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .prepeers-video__poster,
  .prepeers-video__play-circle,
  .prepeers-video__close-button {
    transition: none;
  }
}
</style>
